(() => {
  const selector = '[data-rr-parts-finder]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileLayout = window.matchMedia('(max-width: 749px)');

  const initialise = (section) => {
    if (!section || section.dataset.rrPartsReady === 'true') return;

    const hotspots = [...section.querySelectorAll('[data-rr-hotspot]')];
    const pins = [...section.querySelectorAll('[data-rr-pin-link]')];
    const mobileParts = [...section.querySelectorAll('[data-rr-mobile-part]')];
    const lines = [...section.querySelectorAll('[data-rr-part-line]')];
    const viewport = section.querySelector('[data-rr-parts-viewport]');
    const keys = pins.map((pin) => pin.dataset.rrPartKey).filter(Boolean);
    if (!keys.length) return;

    section.dataset.rrPartsReady = 'true';

    let activeKey = section.querySelector('[data-rr-part-key].is-active')?.dataset.rrPartKey || keys[0];
    let cycleTimer = null;
    let resumeTimer = null;
    let userIsScrolling = false;

    const keyedElements = [...hotspots, ...pins, ...mobileParts, ...lines];

    const setActive = (key) => {
      if (!key) return;
      activeKey = key;

      keyedElements.forEach((element) => {
        const active = element.dataset.rrPartKey === key;
        element.classList.toggle('is-active', active);

        if (element.hasAttribute('data-rr-mobile-part')) {
          element.setAttribute('aria-hidden', active ? 'false' : 'true');
          element.tabIndex = active ? 0 : -1;
        }
      });
    };

    const centreActivePart = (key, behavior = 'smooth') => {
      if (!viewport || viewport.scrollWidth <= viewport.clientWidth || userIsScrolling) return;
      const pin = pins.find((item) => item.dataset.rrPartKey === key);
      if (!pin) return;

      const target = Math.max(0, Math.min(
        pin.offsetLeft - (viewport.clientWidth / 2),
        viewport.scrollWidth - viewport.clientWidth
      ));

      viewport.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : behavior });
    };

    const stopCycle = () => {
      window.clearInterval(cycleTimer);
      cycleTimer = null;
    };

    const startCycle = () => {
      stopCycle();
      if (section.dataset.autoCycle !== 'true' || reducedMotion.matches || mobileLayout.matches || keys.length < 2) return;

      const delay = Math.max(Number(section.dataset.cycleInterval) || 4000, 3000);
      cycleTimer = window.setInterval(() => {
        const index = Math.max(keys.indexOf(activeKey), -1);
        const nextKey = keys[(index + 1) % keys.length];
        setActive(nextKey);
      }, delay);
    };

    const pauseForInteraction = () => {
      stopCycle();
      window.clearTimeout(resumeTimer);
    };

    const resumeAfterInteraction = () => {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(startCycle, 2200);
    };

    [...hotspots, ...pins].forEach((element) => {
      const key = element.dataset.rrPartKey;
      element.addEventListener('pointerenter', () => {
        pauseForInteraction();
        setActive(key);
      });
      element.addEventListener('pointerleave', resumeAfterInteraction);
      element.addEventListener('focus', () => {
        pauseForInteraction();
        setActive(key);
        centreActivePart(key);
      });
      element.addEventListener('blur', resumeAfterInteraction);
      element.addEventListener('pointerdown', () => setActive(key), { passive: true });
      element.addEventListener('click', (event) => {
        if (!mobileLayout.matches || !element.hasAttribute('data-rr-pin-link')) return;
        event.preventDefault();
        pauseForInteraction();
        setActive(key);
      });
    });

    mobileParts.forEach((element) => {
      element.addEventListener('focus', pauseForInteraction);
      element.addEventListener('blur', resumeAfterInteraction);
    });

    if (viewport) {
      let scrollTimer = null;
      viewport.addEventListener('pointerdown', () => {
        userIsScrolling = true;
        pauseForInteraction();
      }, { passive: true });
      viewport.addEventListener('scroll', () => {
        userIsScrolling = true;
        pauseForInteraction();
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          userIsScrolling = false;
          resumeAfterInteraction();
        }, 500);
      }, { passive: true });
      viewport.addEventListener('pointerup', () => {
        userIsScrolling = false;
        resumeAfterInteraction();
      }, { passive: true });
    }

    const visibilityObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              section.classList.add('is-ready');
              startCycle();
            } else {
              stopCycle();
            }
          });
        }, { threshold: 0.22 })
      : null;

    if (visibilityObserver) visibilityObserver.observe(section);
    else {
      section.classList.add('is-ready');
      startCycle();
    }

    const onMotionPreferenceChange = () => {
      if (reducedMotion.matches) stopCycle();
      else startCycle();
    };

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', onMotionPreferenceChange);
    }
    if (typeof mobileLayout.addEventListener === 'function') {
      mobileLayout.addEventListener('change', onMotionPreferenceChange);
    }

    section.addEventListener('shopify:block:select', (event) => {
      const key = event.target?.dataset?.rrPartKey;
      if (!key) return;
      pauseForInteraction();
      setActive(key);
      centreActivePart(key);
    });

    setActive(activeKey);
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(selector)) initialise(scope);
    scope.querySelectorAll?.(selector).forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
